/**
 * Every route the application can navigate to.
 *
 * Centralised so links cannot drift from the router definition; feature
 * routes are added here by the phase that introduces them.
 */
export const ROUTES = {
  home: '/',
  status: '/status',

  // Public authentication screens.
  login: '/login',
  register: '/register',
  verifyEmail: '/verify-email',
  resendVerification: '/resend-verification',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',

  // Authenticated screens.
  dashboard: '/app',
  profile: '/app/profile',
  sessions: '/app/sessions',
  unauthorized: '/app/unauthorized',

  // Customer onboarding and self-service (Phase 2).
  customerOnboarding: '/app/customer/onboarding',
  customerProfile: '/app/customer/profile',
  customerAddresses: '/app/customer/addresses',
  customerConsents: '/app/customer/consents',
  customerProducts: '/app/customer/products',
  customerPlans: '/app/customer/plans',
  customerApply: '/app/customer/apply',
  customerApplications: '/app/customer/applications',
  customerContracts: '/app/customer/contracts',
  customerBankAccounts: '/app/customer/bank-accounts',
  customerMandateSetup: '/app/customer/contracts/:contractId/mandate/setup',
  customerMandate: '/app/customer/mandates/:id',
  customerRepayments: '/app/customer/repayments',
  customerPayments: '/app/customer/payments',
  customerPaymentAuthorize: '/app/customer/payments/authorize/:authorizationId',
  customerPaymentAuthorizeToken: '/app/customer/payments/authorize',
  customerPaymentReceipt: '/app/customer/payments/:id/receipt',
  customerTimeline: '/app/customer/timeline',

  // Administration.
  adminUsers: '/app/admin/users',
  adminUserCreate: '/app/admin/users/new',
  adminRoles: '/app/admin/roles',
  adminAuditLogs: '/app/admin/audit-logs',
  adminCustomers: '/app/admin/customers',
  adminProducts: '/app/admin/products',
  adminProductCreate: '/app/admin/products/new',
  adminRepaymentPlans: '/app/admin/repayment-plans',
  adminRepaymentPlanCreate: '/app/admin/repayment-plans/new',
  adminApplications: '/app/admin/applications',
  adminDiscountsReview: '/app/admin/discounts-review',
  adminContracts: '/app/admin/contracts',
  adminMandates: '/app/admin/mandates',
  adminMandateCancellations: '/app/admin/mandate-cancellations',
  adminProviderBanks: '/app/admin/payment-provider/banks',
  adminBilling: '/app/admin/billing',
  adminBillingRepayment: '/app/admin/billing/repayments/:id',
  adminPaymentAttempts: '/app/admin/payment-attempts',
  adminPaymentAttempt: '/app/admin/payment-attempts/:id',
  adminPayments: '/app/admin/payments',
  adminPayment: '/app/admin/payments/:id',
  adminPaymentReceipt: '/app/admin/payments/:id/receipt',
  adminReports: '/app/admin/reports',
  adminNotificationOutbox: '/app/admin/notifications/outbox',
  adminSystemHealth: '/app/admin/system-health',
} as const;

export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES];

/** Convenience alias so components can read `paths.login` rather than shouting. */
export const paths = ROUTES;

/** Builders for routes that carry a parameter. */
export const routeTo = {
  adminUser: (id: string): string => `/app/admin/users/${id}`,
  adminRole: (id: string): string => `/app/admin/roles/${id}`,
  auditLog: (id: string): string => `/app/admin/audit-logs/${id}`,
  adminCustomer: (id: string): string => `/app/admin/customers/${id}`,
  adminProduct: (id: string): string => `/app/admin/products/${id}`,
  adminRepaymentPlan: (id: string): string => `/app/admin/repayment-plans/${id}`,
  adminApplication: (id: string): string => `/app/admin/applications/${id}`,
  customerProduct: (id: string): string => `/app/customer/products/${id}`,
  customerApplication: (id: string): string => `/app/customer/applications/${id}`,
  customerApplicationEdit: (id: string): string =>
    `/app/customer/apply?applicationId=${encodeURIComponent(id)}&step=terms`,
  customerContract: (id: string): string => `/app/customer/contracts/${id}`,
  customerMandateSetup: (contractId: string): string =>
    `/app/customer/contracts/${contractId}/mandate/setup`,
  customerMandate: (id: string): string => `/app/customer/mandates/${id}`,
  customerPaymentAuthorize: (authorizationId: string): string =>
    `/app/customer/payments/authorize/${authorizationId}`,
  customerPaymentAuthorizeForSchedule: (scheduleId: string): string =>
    `/app/customer/payments/authorize?scheduleId=${encodeURIComponent(scheduleId)}`,
  customerPaymentHistory: (): string => '/app/customer/payments',
  customerPaymentReceipt: (id: string): string => `/app/customer/payments/${id}/receipt`,
  adminMandate: (id: string): string => `/app/admin/mandates/${id}`,
  adminMandateCancellation: (id: string): string => `/app/admin/mandate-cancellations/${id}`,
  adminBillingRepayment: (id: string): string => `/app/admin/billing/repayments/${id}`,
  adminPaymentAttempt: (id: string): string => `/app/admin/payment-attempts/${id}`,
  adminPayment: (id: string): string => `/app/admin/payments/${id}`,
  adminPaymentReceipt: (id: string): string => `/app/admin/payments/${id}/receipt`,
};
